import type { ImageContentFit } from "expo-image";
/* ----------------- UI Components --------------- */
import { useMemo, useState } from "react";
import { ScrollView, type ViewStyle, useWindowDimensions } from "react-native";
import { Text } from "react-native-paper";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { ScrollButtonFlatlist } from "../ChatUI/ScrollButtonFlatlist";
import { ZoomImage } from "../MarkdownRenderer/ZoomImage";
import {
	DEFAULT_MAX_SINGLE_IMAGE_HEIGHT,
	DEFAULT_MAX_SINGLE_IMAGE_WIDTH,
	calculateCardsToShow,
	getSingleImageContainerWidth,
} from "./helpers";

export type ImageType = {
	id: string;
	url: string;
	name?: string;
	caption?: string;
};

interface Props {
	images: ImageType[];
	containerStyle?: ViewStyle;
	useResponsiveImageWidthWeb?: boolean;
	contentFit?: ImageContentFit;
	customImageHeight?: number;
	customImageWidth?: number;
	maxSingleImageHeight?: number;
	maxSingleImageContainerWidth?: number;
	minSingleImageHeight?: number;
	showImageZoomIndicator?: boolean;
	disableSingleImageSizeLimits?: boolean;
	imageBorderRadius?: number;
}

const SIDE_PADDING = 16;

const ImagesCarousel = ({
	images,
	containerStyle,
	useResponsiveImageWidthWeb,
	contentFit,
	customImageHeight,
	customImageWidth,
	maxSingleImageHeight,
	maxSingleImageContainerWidth,
	minSingleImageHeight,
	showImageZoomIndicator,
	disableSingleImageSizeLimits,
	imageBorderRadius = 12,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const { width: windowWidth } = useWindowDimensions();
	const { isMobile } = useDevice();
	const [measuredContainerWidth, setMeasuredContainerWidth] = useState(0);

	const effectiveMaxSingleImageHeight = disableSingleImageSizeLimits
		? maxSingleImageHeight
		: (maxSingleImageHeight ?? DEFAULT_MAX_SINGLE_IMAGE_HEIGHT);
	const effectiveMaxSingleImageContainerWidth = disableSingleImageSizeLimits
		? maxSingleImageContainerWidth
		: (maxSingleImageContainerWidth ?? DEFAULT_MAX_SINGLE_IMAGE_WIDTH);

	const isSingle = images.length === 1;
	const containerWidth = getSingleImageContainerWidth({
		layoutWidth: measuredContainerWidth,
		windowWidth,
		sidePadding: SIDE_PADDING,
		maxWidth: effectiveMaxSingleImageContainerWidth,
	});

	// Multi-image tile sizing
	const imageWidth = useMemo(() => {
		const cardsToShow = calculateCardsToShow(windowWidth);
		return (windowWidth - SIDE_PADDING * (cardsToShow + 1)) / cardsToShow;
	}, [windowWidth]);

	const imageHeight = isMobile ? 200 : customImageHeight || 300;

	const buildZoomProps = (index: number) =>
		isSingle
			? {
					autoSize: true as const,
					containerWidth,
					maxHeight: effectiveMaxSingleImageHeight,
					minHeight: minSingleImageHeight,
					showZoomIndicator: showImageZoomIndicator,
					contentFit: contentFit ?? "contain",
					initialIndex: 0,
				}
			: {
					width:
						isMobile || useResponsiveImageWidthWeb
							? imageWidth
							: customImageWidth || 300,
					height: imageHeight,
					contentFit: contentFit ?? "cover",
					initialIndex: index,
				};

	const renderCaption = (caption: string | undefined) =>
		caption ? (
			<Box maxWidth={containerWidth}>
				<Text
					variant="bodyMedium"
					style={{
						textAlign: "center",
					}}
				>
					{caption}
				</Text>
			</Box>
		) : null;

	if (isMobile) {
		return (
			<Box
				width="100%"
				onLayout={(event) => {
					setMeasuredContainerWidth(event.nativeEvent.layout.width);
				}}
			>
				<ScrollView
					horizontal
					showsHorizontalScrollIndicator
					contentContainerStyle={{
						paddingHorizontal: isSingle ? 0 : SIDE_PADDING,
						paddingBottom: 8,
						gap: isSingle ? 0 : 16,
						justifyContent: "center",
						alignItems: "center",
						width: isSingle || images.length <= 2 ? "100%" : "auto",
						...(containerStyle || {}),
					}}
				>
					{images.map((item, index) => (
						<Box key={item.id} gap={4}>
							<ZoomImage
								url={item.url}
								name={item.name}
								allImages={images}
								borderRadius={imageBorderRadius}
								{...buildZoomProps(index)}
							/>
							{renderCaption(item.caption)}
						</Box>
					))}
				</ScrollView>
			</Box>
		);
	}

	return (
		<Box
			width="100%"
			onLayout={(event) => {
				setMeasuredContainerWidth(event.nativeEvent.layout.width);
			}}
		>
			<ScrollButtonFlatlist
				data={images}
				keyExtractor={(item) => item.id}
				renderItem={({ item, index }) => (
					<Box gap={4}>
						<ZoomImage
							url={item.url}
							name={item.name}
							allImages={images}
							borderRadius={imageBorderRadius}
							{...buildZoomProps(index)}
						/>
						{renderCaption(item.caption)}
					</Box>
				)}
				horizontal
				showsHorizontalScrollIndicator
				contentContainerStyle={{
					paddingBottom: 8,
					gap: isSingle ? 0 : 16,
					justifyContent: "center",
					alignItems: "center",
					width: isSingle || images.length <= 2 ? "100%" : "auto",
					...(containerStyle || {}),
				}}
			/>
		</Box>
	);
};

export default ImagesCarousel;
